import React, { useEffect, useState } from 'react';
import { Account } from '../types';
import apiService from '../services/api';
import SchwabAuth from '../components/SchwabAuth';
import AccountPortfolioSetup from '../components/AccountPortfolioSetup';
import TierSpectrumExplainer from '../components/portfolio/TierSpectrumExplainer';
import TierPageBanner from '../components/portfolio/TierPageBanner';

const Settings: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(
    new Set(),
  );
  const [syncingAccounts, setSyncingAccounts] = useState<Set<string>>(
    new Set(),
  );
  const [syncNotifications, setSyncNotifications] = useState<
    Array<{ id: string; message: string; type: 'success' | 'error' }>
  >([]);
  const [updatingAccount, setUpdatingAccount] = useState<Set<string>>(
    new Set(),
  );
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [optionInputs, setOptionInputs] = useState<Record<string, string>>({});
  const [equityInputs, setEquityInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const accountsData = await apiService.getAccounts();
        setAccounts(accountsData);

        // Clear local input state when accounts are refreshed
        setOptionInputs({});
        setEquityInputs({});

        // Auto-sync all visible accounts after loading
        if (accountsData.length > 0) {
          setAutoSyncing(true);
          try {
            console.log('Starting auto-sync for all accounts...');
            const syncResult = await apiService.syncAllAccounts();
            console.log('Auto-sync result:', syncResult);

            // Refresh accounts data to get updated sync status
            console.log('Refreshing accounts data after auto-sync...');
            const refreshedAccounts = await apiService.getAccounts();
            setAccounts(refreshedAccounts);
            addNotification('All accounts synced successfully!', 'success');
          } catch (syncError: any) {
            console.error('Auto-sync failed:', syncError);
            const errorMessage =
              syncError.response?.data?.message ||
              syncError.message ||
              'Auto-sync failed. You can manually sync individual accounts.';
            addNotification(errorMessage, 'error');
          } finally {
            setAutoSyncing(false);
          }
        }
      } catch (err) {
        setError('Failed to load accounts.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const addNotification = (message: string, type: 'success' | 'error') => {
    const id = Date.now().toString();
    setSyncNotifications((prev) => [...prev, { id, message, type }]);
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setSyncNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const togglePositions = (accountId: string) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const handleManualSync = async (accountId: string) => {
    console.log(`Starting manual sync for account: ${accountId}`);
    setSyncingAccounts((prev) => new Set(prev).add(accountId));
    try {
      console.log('Calling apiService.syncAccount...');
      const response = await apiService.syncAccount(accountId);
      console.log('Sync response:', response);

      // Refresh accounts data to get updated sync status
      console.log('Refreshing accounts data...');
      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification('Account synced successfully!', 'success');
    } catch (error: any) {
      console.error('Manual sync failed:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Sync failed. Please try again.';
      addNotification(errorMessage, 'error');
    } finally {
      setSyncingAccounts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleToggleAutoApprove = async (
    accountId: string,
    currentValue: boolean,
  ) => {
    setUpdatingAccount((prev) => new Set(prev).add(accountId));
    try {
      await apiService.updateAccount(accountId, { autoApprove: !currentValue });

      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification(
        `Auto approve ${!currentValue ? 'enabled' : 'disabled'} successfully!`,
        'success',
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update auto approve setting.';
      addNotification(errorMessage, 'error');
    } finally {
      setUpdatingAccount((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleToggleAutoExit = async (
    accountId: string,
    currentValue: boolean,
  ) => {
    setUpdatingAccount((prev) => new Set(prev).add(accountId));
    try {
      await apiService.updateAccount(accountId, {
        autoExit: !currentValue,
      });

      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification(
        `Auto exit ${!currentValue ? 'enabled' : 'disabled'} successfully!`,
        'success',
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update auto exit setting.';
      addNotification(errorMessage, 'error');
    } finally {
      setUpdatingAccount((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleToggleActive = async (
    accountId: string,
    currentValue: boolean,
  ) => {
    setUpdatingAccount((prev) => new Set(prev).add(accountId));
    try {
      await apiService.updateAccount(accountId, { isActive: !currentValue });

      // Refresh accounts data
      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification(
        `Account ${!currentValue ? 'activated' : 'deactivated'} successfully!`,
        'success',
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update account status.';
      addNotification(errorMessage, 'error');
    } finally {
      setUpdatingAccount((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleMaxUtilizationChange = async (
    accountId: string,
    newValue: number,
  ) => {
    setUpdatingAccount((prev) => new Set(prev).add(accountId));
    try {
      await apiService.updateAccount(accountId, { maxUtilization: newValue });

      // Refresh accounts data
      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification(
        `Max utilization updated to ${(newValue * 100).toFixed(1)}%`,
        'success',
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update max utilization.';
      addNotification(errorMessage, 'error');
    } finally {
      setUpdatingAccount((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleMaxOptionPositionChange = async (
    accountId: string,
    newValue: number,
  ) => {
    setUpdatingAccount((prev) => new Set(prev).add(accountId));
    try {
      await apiService.updateAccount(accountId, {
        maxOptionPosition: newValue,
      });

      // Refresh accounts data
      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification(
        `Max option position updated to $${newValue.toLocaleString()}`,
        'success',
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update max option position.';
      addNotification(errorMessage, 'error');
    } finally {
      setUpdatingAccount((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const handleMaxEquityPositionChange = async (
    accountId: string,
    newValue: number,
  ) => {
    setUpdatingAccount((prev) => new Set(prev).add(accountId));
    try {
      await apiService.updateAccount(accountId, {
        maxEquityPosition: newValue,
      });

      // Refresh accounts data
      const refreshedAccounts = await apiService.getAccounts();
      setAccounts(refreshedAccounts);
      addNotification(
        `Max equity position updated to $${newValue.toLocaleString()}`,
        'success',
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to update max equity position.';
      addNotification(errorMessage, 'error');
    } finally {
      setUpdatingAccount((prev) => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toLocaleString()}`;
  };

  const formatPercentage = (value: number) => {
    if (isNaN(value)) return '0.00%';
    return `${(value * 100).toFixed(2)}%`;
  };

  const getPositionTypeColor = (positionType: string) => {
    return positionType === 'LONG'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const getAssetTypeColor = (assetType: string) => {
    const colors = {
      EQUITY: 'bg-blue-100 text-blue-800',
      OPTION: 'bg-purple-100 text-purple-800',
      MUTUAL_FUND: 'bg-yellow-100 text-yellow-800',
      CASH_EQUIVALENT: 'bg-gray-100 text-gray-800',
      BOND: 'bg-indigo-100 text-indigo-800',
    };
    return (
      colors[assetType as keyof typeof colors] || 'bg-gray-100 text-gray-800'
    );
  };

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Configure enabled tiers"
        description="Enable Tier 1 macro and Tier 6 options slots per account. Tiers 2–5 appear on the spectrum for context."
        settingsHint={false}
      />
      <TierSpectrumExplainer />
      {/* Notifications */}
      {syncNotifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {syncNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`px-4 py-3 rounded-md shadow-lg max-w-sm ${
                notification.type === 'success'
                  ? 'bg-green-100 border border-green-400 text-green-700'
                  : 'bg-red-100 border border-red-400 text-red-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {notification.message}
                </span>
                <button
                  onClick={() =>
                    setSyncNotifications((prev) =>
                      prev.filter((n) => n.id !== notification.id),
                    )
                  }
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">
          Configure your Schwab connection and manage your trading accounts
        </p>
      </div>

      {/* Schwab Authentication Section */}
      <SchwabAuth />

      {/* Accounts Management Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Account Management
          </h2>
          {autoSyncing && (
            <div className="text-sm text-blue-600">
              🔄 Auto-syncing all accounts...
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            Loading accounts...
          </div>
        ) : autoSyncing ? (
          <div className="text-center py-12 text-gray-500">
            <div>Syncing all accounts...</div>
            <div className="text-sm text-gray-400 mt-2">
              This may take a few moments
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No accounts found. Connect your Schwab account above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                    {autoSyncing && (
                      <span className="ml-2 text-blue-500">🔄</span>
                    )}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Value
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cash
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buying Power
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {accounts.map((account) => (
                  <React.Fragment key={account.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => togglePositions(account.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {expandedAccounts.has(account.id) ? '▼' : '▶'}
                          </button>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {account.nickname}
                              {!account.isActive && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Inactive
                                </span>
                              )}
                              {account.autoApprove && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Auto Approve
                                </span>
                              )}
                              {account.autoExitEffective && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  Auto Exit
                                  {account.autoExit == null && account.autoApprove
                                    ? ' (inherited)'
                                    : ''}
                                </span>
                              )}
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Max: {(account.maxUtilization * 100).toFixed(0)}
                                %
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {account.accountNumber}
                            </div>
                            <div className="text-xs text-gray-400">
                              {account.broker}
                            </div>
                            {account.lastSyncAt && (
                              <div className="text-xs text-gray-400 mt-1">
                                Last sync:{' '}
                                {new Date(account.lastSyncAt).toLocaleString()}
                                {account.lastSyncError && (
                                  <span className="text-red-500 ml-1">
                                    • Failed
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {account.accountType}
                        </div>
                        {account.accountSubtype && (
                          <div className="text-sm text-gray-500">
                            {account.accountSubtype}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(account.balances.totalValue)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(account.balances.cashBalance)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(account.balances.buyingPower)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (account.utilizationPct ?? 0) > 0.8
                              ? 'bg-red-100 text-red-800'
                              : (account.utilizationPct ?? 0) > 0.6
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {((account.utilizationPct ?? 0) * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col space-y-2">
                          {account.lastSyncError && (
                            <div className="mb-2 text-xs text-red-600">
                              <div className="font-medium">Sync Error</div>
                              <div className="text-red-500">
                                {account.lastSyncError}
                              </div>
                              <div className="text-gray-500 mt-1">
                                Re-authenticate with Schwab above to fix
                              </div>
                            </div>
                          )}
                          <button
                            className={`inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md ${
                              syncingAccounts.has(account.id) || autoSyncing
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                            }`}
                            onClick={() => handleManualSync(account.id)}
                            disabled={
                              syncingAccounts.has(account.id) || autoSyncing
                            }
                          >
                            {syncingAccounts.has(account.id)
                              ? 'Syncing...'
                              : autoSyncing
                                ? 'Auto-syncing...'
                                : 'Sync Now'}
                          </button>
                          <div className="flex items-center justify-center space-x-2">
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={account.autoApprove}
                                onChange={() =>
                                  handleToggleAutoApprove(
                                    account.id,
                                    account.autoApprove,
                                  )
                                }
                                disabled={
                                  updatingAccount.has(account.id) || autoSyncing
                                }
                                className="mr-1 h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="text-gray-600">Auto Approve</span>
                            </label>
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={account.autoExitEffective}
                                onChange={() =>
                                  handleToggleAutoExit(
                                    account.id,
                                    account.autoExitEffective,
                                  )
                                }
                                disabled={
                                  updatingAccount.has(account.id) || autoSyncing
                                }
                                className="mr-1 h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="text-gray-600">Auto Exit</span>
                            </label>
                            <label className="flex items-center text-xs">
                              <input
                                type="checkbox"
                                checked={account.isActive}
                                onChange={() =>
                                  handleToggleActive(
                                    account.id,
                                    account.isActive,
                                  )
                                }
                                disabled={
                                  updatingAccount.has(account.id) || autoSyncing
                                }
                                className="mr-1 h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="text-gray-600">Active</span>
                            </label>
                          </div>
                          <div className="flex flex-col space-y-1">
                            <label className="text-xs text-gray-600" title="Max fraction of NAV to deploy across tiers">
                              Max util (NAV)
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={account.maxUtilization}
                              onChange={(e) =>
                                handleMaxUtilizationChange(
                                  account.id,
                                  parseFloat(e.target.value),
                                )
                              }
                              disabled={
                                updatingAccount.has(account.id) || autoSyncing
                              }
                              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <div className="text-xs text-gray-500 text-center">
                              {(account.maxUtilization * 100).toFixed(0)}%
                            </div>
                          </div>
                          <div className="flex flex-col space-y-1">
                            <label className="text-xs text-gray-600">
                              Max Option
                            </label>
                            <input
                              type="text"
                              value={
                                optionInputs[account.id] ??
                                account.maxOptionPosition.toLocaleString()
                              }
                              onChange={(e) => {
                                const value = e.target.value.replace(/,/g, '');
                                if (value === '' || /^\d+$/.test(value)) {
                                  setOptionInputs((prev) => ({
                                    ...prev,
                                    [account.id]: e.target.value,
                                  }));
                                }
                              }}
                              onBlur={(e) => {
                                const value =
                                  parseInt(
                                    (
                                      e.target as HTMLInputElement
                                    ).value.replace(/,/g, ''),
                                  ) || 0;
                                if (value >= 100 && value <= 1000000) {
                                  handleMaxOptionPositionChange(
                                    account.id,
                                    value,
                                  );
                                } else {
                                  setOptionInputs((prev) => ({
                                    ...prev,
                                    [account.id]:
                                      account.maxOptionPosition.toLocaleString(),
                                  }));
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  const value =
                                    parseInt(
                                      (
                                        e.target as HTMLInputElement
                                      ).value.replace(/,/g, ''),
                                    ) || 0;
                                  if (value >= 100 && value <= 1000000) {
                                    handleMaxOptionPositionChange(
                                      account.id,
                                      value,
                                    );
                                  } else {
                                    setOptionInputs((prev) => ({
                                      ...prev,
                                      [account.id]:
                                        account.maxOptionPosition.toLocaleString(),
                                    }));
                                  }
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              disabled={
                                updatingAccount.has(account.id) || autoSyncing
                              }
                              className="w-20 text-xs border border-gray-300 rounded px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="10,000"
                            />
                            <div className="text-xs text-gray-400">
                              $1k - $100k
                            </div>
                          </div>
                          <div className="flex flex-col space-y-1">
                            <label className="text-xs text-gray-600">
                              Max Equity
                            </label>
                            <input
                              type="text"
                              value={
                                equityInputs[account.id] ??
                                account.maxEquityPosition.toLocaleString()
                              }
                              onChange={(e) => {
                                const value = e.target.value.replace(/,/g, '');
                                if (value === '' || /^\d+$/.test(value)) {
                                  setEquityInputs((prev) => ({
                                    ...prev,
                                    [account.id]: e.target.value,
                                  }));
                                }
                              }}
                              onBlur={(e) => {
                                const value =
                                  parseInt(
                                    (
                                      e.target as HTMLInputElement
                                    ).value.replace(/,/g, ''),
                                  ) || 0;
                                if (value >= 500 && value <= 500000) {
                                  handleMaxEquityPositionChange(
                                    account.id,
                                    value,
                                  );
                                } else {
                                  setEquityInputs((prev) => ({
                                    ...prev,
                                    [account.id]:
                                      account.maxEquityPosition.toLocaleString(),
                                  }));
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  const value =
                                    parseInt(
                                      (
                                        e.target as HTMLInputElement
                                      ).value.replace(/,/g, ''),
                                    ) || 0;
                                  if (value >= 500 && value <= 500000) {
                                    handleMaxEquityPositionChange(
                                      account.id,
                                      value,
                                    );
                                  } else {
                                    setEquityInputs((prev) => ({
                                      ...prev,
                                      [account.id]:
                                        account.maxEquityPosition.toLocaleString(),
                                    }));
                                  }
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              disabled={
                                updatingAccount.has(account.id) || autoSyncing
                              }
                              className="w-20 text-xs border border-gray-300 rounded px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="25,000"
                            />
                            <div className="text-xs text-gray-400">
                              $5k - $500k
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Positions Table */}
                    {expandedAccounts.has(account.id) && (
                      <>
                        <tr>
                          <td colSpan={8} className="px-0 py-0">
                            <div className="bg-gray-50 border-t border-gray-200">
                              <div className="px-6 py-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">
                                  Positions ({account.positions.length})
                                </h4>
                                {account.positions.length === 0 ? (
                                  <div className="text-center py-4 text-gray-500">
                                    No positions found
                                  </div>
                                ) : (
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead>
                                        <tr>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Symbol
                                          </th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                          </th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quantity
                                          </th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Avg Price
                                          </th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Market Value
                                          </th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Cost Basis
                                          </th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Unrealized P&L
                                          </th>
                                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            P&L %
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-100">
                                        {account.positions.map(
                                          (position, index) => (
                                            <tr
                                              key={`${account.id}-${position.symbol}-${index}`}
                                              className="hover:bg-gray-50"
                                            >
                                              <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                  {position.symbol}
                                                </div>
                                                {position.description && (
                                                  <div className="text-xs text-gray-500">
                                                    {position.description}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex space-x-1">
                                                  <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPositionTypeColor(position.positionType)}`}
                                                  >
                                                    {position.positionType}
                                                  </span>
                                                  <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAssetTypeColor(position.assetType)}`}
                                                  >
                                                    {position.assetType.replace(
                                                      '_',
                                                      ' ',
                                                    )}
                                                  </span>
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <div className="text-sm text-gray-900">
                                                  {position.quantity.toLocaleString()}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <div className="text-sm text-gray-900">
                                                  {formatCurrency(
                                                    position.averagePrice,
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <div className="text-sm font-medium text-gray-900">
                                                  {formatCurrency(
                                                    position.marketValue,
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <div className="text-sm text-gray-900">
                                                  {formatCurrency(
                                                    position.costBasis,
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <div
                                                  className={`text-sm font-medium ${position.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                                >
                                                  {formatCurrency(
                                                    position.unrealizedPL,
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <div
                                                  className={`text-sm font-medium ${position.unrealizedPLPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                                >
                                                  {formatPercentage(
                                                    position.unrealizedPLPercent,
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={8} className="px-0 py-0">
                            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                              <AccountPortfolioSetup
                                account={account}
                                onNotify={addNotification}
                                onSaved={(updated) => {
                                  setAccounts((prev) =>
                                    prev.map((a) =>
                                      a.id === updated.id
                                        ? { ...a, ...updated, riskTiers: updated.riskTiers }
                                        : a,
                                    ),
                                  );
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default Settings;
